import React, { useState, useEffect, useRef, useContext, createContext } from 'react';
import { createPortal } from 'react-dom';

// A single, opt-in floating popup that collects the /mcc (Frank) page's DEBUG displays -- starting with a
// section's "Chosen material dimensions" -- out of the main flow, so the customer-facing UX can be designed
// cleanly. Any component deep in the tree pushes content in with <DebugPanel>; because it all funnels through
// ONE provider, dropping <DebugPopupProvider> (or turning off its show_frank_debug_popup gate) hides every debug
// display at once when the real UX is ready.
//
// The panel is MOVABLE: drag it by its header (docked bottom-right until first grabbed), and collapse it to just
// the header bar. containerElement is the popup's scrollable body node; <DebugPanel> portals into it. `enabled`
// distinguishes "no popup on this page" (legacy / popup off -> render inline, unchanged) from "popup present but
// its body is collapsed / not mounted yet" (-> render nothing, it belongs in the popup).
const DebugPopupContext = createContext({ enabled: false, containerElement: null });

// Per-user opt-in, off by default: the show_frank_debug_popup UserParam (window.show_frank_debug_popup, emitted by the Frank
// edit view). Decoupled from admin / quote_builder_v2_enabled, so the debug popup is turned on for specific
// users rather than for every admin.
const canSeeDebug = () => Boolean(window.show_frank_debug_popup);

const panelStyle = {
  display: 'inline-flex', flexDirection: 'column', background: '#fff', border: '1px solid #d1d5db',
  borderRadius: '8px', boxShadow: '0 6px 24px rgba(0,0,0,0.2)', overflow: 'hidden',
};
const headerStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', background: '#1f2937',
  color: '#e5e7eb', padding: '8px 12px', fontSize: '12px', fontFamily: 'monospace', fontWeight: 'bold',
  cursor: 'move', userSelect: 'none',
};
const collapseButtonStyle = {
  background: 'none', border: 'none', color: '#e5e7eb', cursor: 'pointer', fontSize: '16px', lineHeight: 1,
  padding: '0 4px',
};
const bodyStyle = {
  width: '380px', maxWidth: '90vw', maxHeight: '60vh', padding: '12px', overflow: 'auto', background: '#fff',
};
const sectionTitleStyle = {
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af',
  fontFamily: 'monospace', marginBottom: '6px', borderBottom: '1px solid #eee', paddingBottom: '4px',
};

// Wrap the page ONCE. Renders the children unchanged, plus (when show_frank_debug_popup is on) the movable floating
// debug popup they push content into. When it is off it is a pass-through, so nothing debug-related renders.
export const DebugPopupProvider = ({ children }) => {
  const [open, setOpen] = useState(true);
  const [containerElement, setContainerElement] = useState(null);
  // null -> docked bottom-right (the default); { x, y } once the viewer has dragged it, so it stays put.
  const [position, setPosition] = useState(null);
  // Cursor-to-panel-top-left offset held in a ref so following the cursor does not re-render per mousemove.
  const dragOffset = useRef(null);
  const wrapperRef = useRef(null);

  // Follow the cursor on document-level listeners (so a drag continues even if the cursor leaves the header),
  // and do nothing while not dragging (dragOffset null), so plain mouse movement never re-renders.
  useEffect(() => {
    if (!canSeeDebug()) {
      return undefined;
    }
    const onMouseMove = (event) => {
      if (!dragOffset.current) {
        return;
      }
      setPosition({ x: event.clientX - dragOffset.current.x, y: event.clientY - dragOffset.current.y });
    };
    const onMouseUp = () => { dragOffset.current = null; };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  if (!canSeeDebug()) {
    return children;
  }

  // Pin to the current on-screen pixel position (measured from the wrapper), which also switches the docked
  // bottom-right default over to free top/left positioning, then track the cursor.
  const startDrag = (event) => {
    const rect = wrapperRef.current.getBoundingClientRect();
    dragOffset.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    setPosition({ x: rect.left, y: rect.top });
    event.preventDefault();
  };

  const wrapperStyle = position
    ? { position: 'fixed', top: `${position.y}px`, left: `${position.x}px`, zIndex: 5000 }
    : { position: 'fixed', right: '16px', bottom: '16px', zIndex: 5000 };

  return (
    <DebugPopupContext.Provider value={{ enabled: true, containerElement }}>
      {children}
      <div ref={wrapperRef} style={wrapperStyle}>
        <div style={panelStyle}>
          <div onMouseDown={startDrag} style={headerStyle}>
            <span>🐛 Debug</span>
            <button
              type="button"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={() => setOpen((wasOpen) => !wasOpen)}
              style={collapseButtonStyle}
              title={open ? 'Collapse' : 'Expand'}
            >
              {open ? '–' : '+'}
            </button>
          </div>
          {open && <div ref={setContainerElement} style={bodyStyle} />}
        </div>
      </div>
    </DebugPopupContext.Provider>
  );
};

// Push a labelled block into the debug popup. Where there is no popup (the legacy page, or the popup off) it
// falls back to rendering inline, so those surfaces are unchanged.
export const DebugPanel = ({ title, children }) => {
  const { enabled, containerElement } = useContext(DebugPopupContext);

  if (!enabled) {
    return children || null;
  }

  if (!containerElement || !children) {
    return null;
  }

  return createPortal(
    <div style={{ marginBottom: '14px' }}>
      {title && <div style={sectionTitleStyle}>{title}</div>}
      {children}
    </div>,
    containerElement
  );
};

export default DebugPopupProvider;
